/**
 * The customer's notification inbox.
 *
 * Reads through `api/notifications.ts` (portal-local data, PostgREST under
 * RLS — no BFF hop, see that module's doc). Newest-first ordering is the
 * data layer's job; this page renders whatever order it receives.
 *
 * ⚠️ Deliberately does not branch on `notification.kind` — see the module
 * doc on `NotificationRow.tsx` and `.claude/skills/notifications/SKILL.md`.
 *
 * Three outcomes, and they must not be confused with one another:
 *   - a genuinely empty inbox           → the existing `EmptyState`
 *   - the read failed                   → an honest error, never rendered as
 *                                          "no messages" (a missed pickup is
 *                                          not the same as nothing happened)
 *   - rows                              → newest-first, unread styled
 *                                          differently from read
 */
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { EmptyState } from '../components/EmptyState.tsx';
import { NotificationRow } from '../components/NotificationRow.tsx';
import { IconAlert, IconBell } from '../components/icons.tsx';
import { getSupabaseClient } from '../api/supabase.ts';
import { resolveSafeDeepLink } from '../push/swLogic.ts';
import {
  listNotifications,
  markNotificationRead,
  UNREAD_COUNT_QUERY_KEY,
  type Notification,
} from '../api/notifications.ts';
import styles from '../components/NotificationRow.module.css';

/** Shared with `pages/DashboardLayout.tsx`'s unread-count query key prefix. */
export const NOTIFICATIONS_QUERY_KEY = ['notifications'] as const;


async function loadNotifications(): Promise<Notification[]> {
  const result = await listNotifications(getSupabaseClient());
  if (result.error) throw new Error(result.error);
  return result.data;
}

export function NotificacoesList(): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: NOTIFICATIONS_QUERY_KEY,
    queryFn: loadNotifications,
  });

  const markRead = useMutation({
    mutationFn: (id: string) => markNotificationRead(getSupabaseClient(), id),
    // Optimistic, and deliberately not awaited by the caller below: the row
    // must read as opened immediately, not after a round trip to Postgres.
    onMutate: (id: string) => {
      queryClient.setQueryData<Notification[]>(NOTIFICATIONS_QUERY_KEY, (prev) =>
        prev?.map((n) =>
          n.id === id && n.readAt === null ? { ...n, readAt: new Date().toISOString(), state: 'read' } : n,
        ),
      );
    },
    onSettled: () => {
      // The tab-bar badge (`DashboardLayout.tsx`) owns this query; refresh it
      // rather than guess the new count here.
      void queryClient.invalidateQueries({ queryKey: UNREAD_COUNT_QUERY_KEY });
    },
  });

  const handleOpen = useCallback(
    (notification: Notification) => {
      if (notification.readAt === null) {
        markRead.mutate(notification.id);
      }

      const target = resolveSafeDeepLink(notification.deepLink);
      if (target) navigate(target);
    },
    [markRead, navigate],
  );

  if (isLoading) {
    return (
        <p className={styles['status']}>{t('notifications.loading')}</p>
    );
  }

  if (isError) {
    return (
        <div className="ns-alert" role="alert">
          <IconAlert size={18} />
          <div>
            <p className={styles['error-title']}>{t('notifications.loadErrorTitle')}</p>
            <p className={styles['error-body']}>{t('notifications.loadErrorBody')}</p>
            <button type="button" className={`ns-cta ${styles['error-retry']}`} onClick={() => void refetch()}>
              {t('notifications.retry')}
            </button>
          </div>
        </div>
    );
  }

  const notifications = data ?? [];

  if (notifications.length === 0) {
    return (
        <EmptyState
          icon={<IconBell size={32} />}
          title={t('empty.notifications.title')}
          description={t('empty.notifications.body')}
        />
    );
  }

  return (
      <ul className={styles['list']}>
        {notifications.map((notification) => (
          <NotificationRow key={notification.id} notification={notification} onOpen={handleOpen} />
        ))}
      </ul>
  );
}
